/**
 * THE PDF of an invoice or quote — fetched, never drawn.
 *
 * There is exactly ONE document generator: crm-api's `InvoicePdfRenderer`
 * (xop-brain), served at `GET /api/v1/invoices/:id/pdf`. This file used to be
 * `pdf-generator.ts`, a jsPDF copy of the layout, and it was one of THREE
 * renderers (this one, the reseller portal's fork, crm-api's own). They drifted:
 * the admins' review mail of an invoice carried a different logo and different
 * formatting from the invoice in the CRM, and even the CRM's own list-page
 * downloads filled gaps with other defaults than its detail page (CEO,
 * 2026-09-17). So the browser only ever SHOWS or SAVES bytes crm-api rendered —
 * the preview, the download, the share mail and the review copy are one file.
 *
 * Do not reintroduce a client-side generator. Layout changes go in
 * `InvoicePdfRenderer`; see docs/crm.md → "One invoice generator".
 */
import api from "./api"
import { saveBlob } from "../../lib/download"
import { shareDocumentPdfFilename } from "./shareDocumentDefaults"

export interface DocumentPdf {
  bytes: ArrayBuffer
  /** From crm-api's Content-Disposition; null when the header is unreadable. */
  filename: string | null
}

/**
 * The filename out of a Content-Disposition header. RFC 5987 `filename*` wins
 * (it carries non-ASCII company names intact), then the plain `filename`.
 */
export function filenameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null

  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header)
  if (extended?.[2]) {
    try {
      return decodeURIComponent(extended[2].trim())
    } catch {
      // fall through to the plain parameter
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header)
  const value = (plain?.[2] ?? plain?.[1] ?? "").trim()
  return value.length > 0 ? value : null
}

/** Fetch the rendered PDF of document `id` (an invoice or a quote). */
export async function fetchDocumentPdf(id: string | number): Promise<DocumentPdf> {
  const response = await api.get(`/invoices/${id}/pdf`, { responseType: "arraybuffer" })
  const headers = response.headers ?? {}
  const disposition = headers["content-disposition"] ?? headers["Content-Disposition"]
  // One call, one header: a non-string value (an axios header array) is unusable.
  return {
    bytes: response.data as ArrayBuffer,
    filename: typeof disposition === "string" ? filenameFromContentDisposition(disposition) : null,
  }
}

/**
 * Save document `id`'s PDF to disk — the native Save As dialog in the desktop
 * app, a normal download on the web (see lib/download.ts). The name is the one
 * crm-api gives the mail attachment; `fallback` covers a proxy that dropped
 * the header.
 */
export async function downloadDocumentPdf(
  id: string | number,
  fallback: { documentType: "invoice" | "quote"; number?: string; company?: string },
): Promise<void> {
  const { bytes, filename } = await fetchDocumentPdf(id)
  const name =
    filename ?? shareDocumentPdfFilename(fallback.documentType, fallback.number, fallback.company)
  await saveBlob(name, new Blob([bytes], { type: "application/pdf" }))
}
